import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { subscriptionsApi } from '../../api/subscriptions';
import { authApi } from '../../api/auth';
import { queryKeys } from '../queryClient';
import type { SubscriptionBillingCycle, SubscriptionPlanInput } from '../../types/domain';

export function useSubscriptionPlans(includeInactive = false) {
  return useQuery({
    queryKey: queryKeys.subscriptionPlans(includeInactive),
    queryFn: () => subscriptionsApi.listPlans(includeInactive),
  });
}

export function useCreatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SubscriptionPlanInput) => subscriptionsApi.createPlan(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscriptionPlans'] }),
  });
}

export function useUpdatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SubscriptionPlanInput }) => subscriptionsApi.updatePlan(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscriptionPlans'] }),
  });
}

export function useDeletePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => subscriptionsApi.deletePlan(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscriptionPlans'] }),
  });
}

export function useTenantSubscription() {
  return useQuery({ queryKey: queryKeys.tenantSubscription, queryFn: () => subscriptionsApi.getMySubscription() });
}

export function useUpgradeSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ planId, billingCycle }: { planId: string; billingCycle: SubscriptionBillingCycle }) =>
      subscriptionsApi.upgrade(planId, billingCycle),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.tenantSubscription });
      qc.invalidateQueries({ queryKey: queryKeys.subscriptionDashboard });
      qc.invalidateQueries({ queryKey: queryKeys.allTenantSubscriptions });
    },
  });
}

export function useCreateCheckout() {
  return useMutation({
    mutationFn: ({ planId, billingCycle }: { planId: string; billingCycle: SubscriptionBillingCycle }) =>
      subscriptionsApi.createCheckout(planId, billingCycle),
  });
}

export function useCreatePortalSession() {
  return useMutation({ mutationFn: () => subscriptionsApi.createPortalSession() });
}

export function useCancelSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => subscriptionsApi.cancel(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.tenantSubscription });
      qc.invalidateQueries({ queryKey: queryKeys.subscriptionDashboard });
      qc.invalidateQueries({ queryKey: queryKeys.allTenantSubscriptions });
    },
  });
}

export function useAllTenantSubscriptions() {
  return useQuery({ queryKey: queryKeys.allTenantSubscriptions, queryFn: () => subscriptionsApi.listAllTenantSubscriptions() });
}

export function useSubscriptionDashboard() {
  return useQuery({ queryKey: queryKeys.subscriptionDashboard, queryFn: () => subscriptionsApi.getDashboard() });
}

export function useSetSubscriptionDiscount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, discountCode, discountPercent }: { tenantId: string; discountCode: string | null; discountPercent: number | null }) =>
      subscriptionsApi.setDiscount(tenantId, discountCode, discountPercent),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.allTenantSubscriptions });
      qc.invalidateQueries({ queryKey: queryKeys.subscriptionDashboard });
    },
  });
}

export function useMarkSubscriptionRefunded() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, reason }: { tenantId: string; reason: string }) => subscriptionsApi.markRefunded(tenantId, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.allTenantSubscriptions }),
  });
}

export function useSetFeatureOverrides() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, features }: { tenantId: string; features: string[] }) => subscriptionsApi.setFeatureOverrides(tenantId, features),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.allTenantSubscriptions });
      qc.invalidateQueries({ queryKey: queryKeys.tenantSubscription });
    },
  });
}

export function useSubscriptionMrrTrend() {
  return useQuery({ queryKey: queryKeys.subscriptionMrrTrend, queryFn: () => subscriptionsApi.getMrrTrend() });
}

export function useSubscriptionChurnTrend() {
  return useQuery({ queryKey: queryKeys.subscriptionChurnTrend, queryFn: () => subscriptionsApi.getChurnTrend() });
}

export function useSubscriptionPlanDistribution() {
  return useQuery({ queryKey: queryKeys.subscriptionPlanDistribution, queryFn: () => subscriptionsApi.getPlanDistribution() });
}

/** Staff seat count for the caller's own tenant — the "users" half of the usage-vs-limit bars
 * on the manager's subscription page. See authApi.getUserCount for why this isn't UsersController. */
export function useStaffUserCount() {
  return useQuery({ queryKey: queryKeys.staffUserCount, queryFn: () => authApi.getUserCount() });
}
